import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Settings } from 'lucide-react';

// Import admin card components
import {
  SmtpConfigCard,
  StripeConfigCard,
  AccountingEmailCard,
  ApiKeysCard,
  TicketEmailCard,
  MobileAppLinksCard
} from '@/components/admin';

export default function AdminSettingsPage() {
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'OBC Cloud',
    smtp_use_tls: true,
    configured: false
  });
  const [stripeConfig, setStripeConfig] = useState({
    stripe_secret_key: '',
    stripe_publishable_key: '',
    configured: false
  });
  const [accountingConfig, setAccountingConfig] = useState({
    accounting_email: '',
    notify_on_purchase: true,
    configured: false
  });
  const [ticketConfig, setTicketConfig] = useState({
    ticket_notification_email: '',
    notify_on_new_ticket: true,
    notify_on_ticket_reply: true,
    configured: false
  });
  const [mobileLinksConfig, setMobileLinksConfig] = useState({
    app_store_url: '',
    play_store_url: ''
  });
  const [apiTokens, setApiTokens] = useState([]);
  const [newApiToken, setNewApiToken] = useState({ name: '', permissions: ['redeem_token'] });
  const [createdApiKey, setCreatedApiKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [smtpRes, stripeRes, accountingRes, ticketRes, apiTokensRes, mobileLinksRes] = await Promise.all([
        adminAPI.getSmtpConfig(),
        adminAPI.getStripeConfig(),
        adminAPI.getAccountingEmailConfig(),
        adminAPI.getTicketEmailConfig(),
        adminAPI.getApiTokens(),
        adminAPI.getMobileLinksConfig()
      ]);
      setSmtpConfig(smtpRes.data);
      setStripeConfig(stripeRes.data);
      setAccountingConfig(accountingRes.data);
      setTicketConfig(ticketRes.data);
      setApiTokens(apiTokensRes.data || []);
      setMobileLinksConfig(mobileLinksRes.data || { app_store_url: '', play_store_url: '' });
    } catch (err) {
      toast.error('Errore nel caricamento configurazione');
    } finally {
      setLoading(false);
    }
  };

  // SMTP handlers
  const handleSaveSmtp = async () => {
    if (!smtpConfig.smtp_host || !smtpConfig.smtp_user || !smtpConfig.smtp_from_email) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    setSaving(true);
    try {
      const res = await adminAPI.updateSmtpConfig(smtpConfig);
      setSmtpConfig(res.data);
      toast.success('Configurazione SMTP salvata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const res = await adminAPI.testSmtpConfig();
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test fallito');
    } finally {
      setTestingSmtp(false);
    }
  };

  // Stripe handlers
  const handleSaveStripe = async () => {
    if (!stripeConfig.stripe_secret_key || stripeConfig.stripe_secret_key.startsWith('sk_****')) {
      toast.error('Inserisci una Secret Key Stripe valida');
      return;
    }
    setSaving(true);
    try {
      const res = await adminAPI.updateStripeConfig(stripeConfig);
      setStripeConfig(res.data);
      toast.success('Configurazione Stripe salvata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Accounting email handlers
  const handleSaveAccountingEmail = async () => {
    if (!accountingConfig.accounting_email) {
      toast.error('Inserisci un\'email di contabilita\'');
      return;
    }
    setSaving(true);
    try {
      const res = await adminAPI.updateAccountingEmailConfig(accountingConfig);
      setAccountingConfig(res.data);
      toast.success('Email contabilita\' salvata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Ticket email handlers
  const handleSaveTicketEmail = async () => {
    if (!ticketConfig.ticket_notification_email) {
      toast.error('Inserisci un\'email per le notifiche ticket');
      return;
    }
    setSaving(true);
    try {
      const res = await adminAPI.updateTicketEmailConfig(ticketConfig);
      setTicketConfig(res.data);
      toast.success('Email notifiche ticket salvata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // API token handlers
  const handleCreateApiToken = async () => {
    if (!newApiToken.name) {
      toast.error('Inserisci un nome per l\'API Key');
      return;
    }
    if (newApiToken.permissions.length === 0) {
      toast.error('Seleziona almeno un permesso');
      return;
    }
    setSaving(true);
    try {
      const res = await adminAPI.createApiToken(newApiToken);
      setCreatedApiKey(res.data.api_key);
      setApiTokens([...apiTokens, res.data]);
      setNewApiToken({ name: '', permissions: ['redeem_token'] });
      toast.success('API Key creata! Copiala ora, non sara\' piu\' visibile.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteApiToken = async (tokenId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa API Key?')) return;
    try {
      await adminAPI.deleteApiToken(tokenId);
      setApiTokens(apiTokens.filter(t => t.token_id !== tokenId));
      toast.success('API Key eliminata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleToggleApiToken = async (tokenId) => {
    try {
      const res = await adminAPI.toggleApiToken(tokenId);
      setApiTokens(apiTokens.map(t => t.token_id === tokenId ? { ...t, is_active: res.data.is_active } : t));
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleSaveMobileLinks = async () => {
    setSaving(true);
    try {
      await adminAPI.updateMobileLinksConfig(mobileLinksConfig);
      toast.success('Link app mobile salvati');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="admin-settings-title">
          <Settings className="w-8 h-8 inline mr-3 text-purple-500" />
          Configurazione Sistema
        </h1>
        <p className="text-muted-foreground mt-1">Gestisci SMTP, Stripe, contabilita' e API Keys (solo root)</p>
      </div>

      {/* SMTP Configuration */}
      <SmtpConfigCard 
        smtpConfig={smtpConfig} 
        setSmtpConfig={setSmtpConfig} 
        onSave={handleSaveSmtp}
        onTest={handleTestSmtp}
        saving={saving}
        testingSmtp={testingSmtp}
      />

      {/* Stripe Configuration */}
      <StripeConfigCard 
        stripeConfig={stripeConfig} 
        setStripeConfig={setStripeConfig} 
        onSave={handleSaveStripe}
        saving={saving}
      />

      {/* Accounting Email Configuration */}
      <AccountingEmailCard 
        accountingConfig={accountingConfig} 
        setAccountingConfig={setAccountingConfig} 
        onSave={handleSaveAccountingEmail}
        saving={saving}
      />

      {/* Ticket Email Configuration */}
      <TicketEmailCard 
        ticketConfig={ticketConfig} 
        setTicketConfig={setTicketConfig} 
        onSave={handleSaveTicketEmail}
        saving={saving}
      />

      {/* Mobile App Links */}
      <MobileAppLinksCard 
        config={mobileLinksConfig} 
        setConfig={setMobileLinksConfig} 
        onSave={handleSaveMobileLinks}
        saving={saving}
      />

      {/* API Keys */}
      <ApiKeysCard 
        apiTokens={apiTokens}
        setApiTokens={setApiTokens}
        newApiToken={newApiToken}
        setNewApiToken={setNewApiToken}
        createdApiKey={createdApiKey}
        setCreatedApiKey={setCreatedApiKey}
        onCreateToken={handleCreateApiToken}
        onDeleteToken={handleDeleteApiToken}
        onToggleToken={handleToggleApiToken}
        saving={saving}
      />
    </div>
  );
}
